import {
  federationConfigLoader,
  getRemoteConfigHostRoutePrefixes,
  resolveRemoteUrl,
  type RemoteConfig,
} from '@/lib/moduleFederation/config/federationConfigLoader';
import {
  type LoadedModuleManifest,
  type ModuleManifest,
  type ModuleManifestMenuItem,
  type ModuleManifestIcon,
  type ModuleManifestRoute,
  type ModuleManifestWidgetItem,
  expandManifestFromPages,
  parseAndResolveHostRoutePrefixes,
  parseModuleManifestWidgets,
  pathUnderAnyHostRoutePrefix,
  sanitizeModuleManifestMenuIcons,
} from '@/lib/moduleFederation/config/moduleManifest';

const MANIFEST_FILENAME = 'module-manifest.json';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface ManifestCache {
  data: LoadedModuleManifest;
  timestamp: number;
}

class ManifestLoader {
  private cache = new Map<string, ManifestCache>();
  private loadPromise: Promise<LoadedModuleManifest[]> | null = null;

  async loadAllManifests(force = false): Promise<LoadedModuleManifest[]> {
    if (!force && this.loadPromise) {
      return this.loadPromise;
    }

    if (!force && this.cache.size > 0) {
      const oldest = Math.min(...Array.from(this.cache.values()).map(c => c.timestamp));
      if (Date.now() - oldest < CACHE_TTL) {
        return Array.from(this.cache.values()).map(c => c.data);
      }
    }

    this.loadPromise = this.fetchAllManifests();
    const results = await this.loadPromise;
    this.loadPromise = null;
    return results;
  }

  private async fetchAllManifests(): Promise<LoadedModuleManifest[]> {
    // Always wait for federation config before reading remotes. Otherwise the first
    // concurrent caller (e.g. useModuleManifests vs _app preload) can see an empty
    // remote map and permanently skip manifest fetches for that in-flight promise.
    await federationConfigLoader.loadConfig();
    const remotes = federationConfigLoader.getAllRemotes();
    const results: LoadedModuleManifest[] = [];

    await Promise.all(
      Object.entries(remotes).map(async ([remoteName, remoteConfig]) => {
        if (remoteConfig.isLibrary) {
          return;
        }

        try {
          const loaded = await this.fetchManifest(remoteName, remoteConfig);
          if (loaded) {
            results.push(loaded);
            this.cache.set(remoteName, { data: loaded, timestamp: Date.now() });
          }
        } catch (error) {
          console.warn(`[ManifestLoader] Failed to load manifest for ${remoteName}:`, error);
        }
      }),
    );

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('module-manifests-loaded', { detail: results }));
    }

    return results;
  }

  private async fetchManifest(remoteName: string, remoteConfig: RemoteConfig): Promise<LoadedModuleManifest | null> {
    const manifestUrl = this.resolveManifestUrl(remoteConfig);
    if (!manifestUrl) {
      return null;
    }

    try {
      const response = await fetch(manifestUrl, {
        cache: 'no-cache',
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (!response.ok) {
        console.warn(`[ManifestLoader] ${remoteName}: HTTP ${response.status} fetching manifest`);
        return null;
      }

      const raw = await response.json();
      const manifest = this.validateManifest(raw, remoteName, remoteConfig);
      if (!manifest) {
        return null;
      }

      const baseUrl = this.getBaseUrl(remoteConfig);

      return {
        remoteName,
        manifest,
        baseUrl,
        loadedAt: Date.now(),
        healthy: true,
      };
    } catch (error) {
      console.warn(`[ManifestLoader] ${remoteName}: fetch error`, error);
      return null;
    }
  }

  private resolveManifestUrl(remoteConfig: RemoteConfig): string | null {
    if (remoteConfig.manifestUrl) {
      return remoteConfig.manifestUrl;
    }

    try {
      const baseUrl = this.getBaseUrl(remoteConfig);
      return `${baseUrl}/${MANIFEST_FILENAME}`;
    } catch {
      return null;
    }
  }

  private getBaseUrl(remoteConfig: RemoteConfig): string {
    const { url } = resolveRemoteUrl(remoteConfig);
    return url.replace(/\/remoteEntry\.js$/, '').replace(/\/$/, '');
  }

  private validateManifest(raw: unknown, remoteName: string, remoteConfig: RemoteConfig): ModuleManifest | null {
    if (!raw || typeof raw !== 'object') {
      console.warn(`[ManifestLoader] ${remoteName}: manifest is not an object`);
      return null;
    }

    let obj = raw as Record<string, unknown>;

    if (typeof obj.name !== 'string' || typeof obj.displayName !== 'string') {
      console.warn(`[ManifestLoader] ${remoteName}: manifest missing name or displayName`);
      return null;
    }

    if (Array.isArray(obj.pages)) {
      const expanded = expandManifestFromPages(obj, remoteName);
      if (!expanded) {
        return null;
      }
      obj = expanded;
    }

    if (!obj.menu || typeof obj.menu !== 'object') {
      console.warn(`[ManifestLoader] ${remoteName}: manifest missing menu`);
      return null;
    }

    const menu = obj.menu as Record<string, unknown>;
    if (!Array.isArray(menu.items)) {
      console.warn(`[ManifestLoader] ${remoteName}: manifest menu.items is not an array`);
      return null;
    }

    if (!Array.isArray(obj.routes)) {
      console.warn(`[ManifestLoader] ${remoteName}: manifest routes is not an array`);
      return null;
    }

    const manifest = obj as unknown as ModuleManifest;

    sanitizeModuleManifestMenuIcons(manifest, remoteName);

    const sanitizeCatalogDescriptions = (item: { description?: string }, itemPath: string): void => {
      if (item.description !== undefined && item.description.trim().length === 0) {
        console.warn(`[ManifestLoader] ${remoteName}: invalid description at ${itemPath}, ignored`);
        delete item.description;
      } else if (typeof item.description === 'string') {
        item.description = item.description.trim();
      }
    };

    const normalizeMenuPaths = (items: ModuleManifestMenuItem[], basePath = 'menu.items'): void => {
      items.forEach((item, index) => {
        const itemPath = `${basePath}[${index}]`;
        sanitizeCatalogDescriptions(item, itemPath);
        if (item.path) {
          item.path = item.path.replace(/\/{2,}/g, '/');
        }
        if (item.children?.length) {
          normalizeMenuPaths(item.children, `${itemPath}.children`);
        }
      });
    };
    normalizeMenuPaths(manifest.menu.items);
    for (const route of manifest.routes) {
      route.path = route.path.replace(/\/{2,}/g, '/');
    }

    const prefixes = parseAndResolveHostRoutePrefixes(obj.hostRoutePrefix, remoteName);
    if (!prefixes) {
      console.warn(
        `[ManifestLoader] ${remoteName}: hostRoutePrefix must be a non-empty absolute path string, or a non-empty array of such strings (each starting with /)`,
      );
      return null;
    }

    const configPrefixes = getRemoteConfigHostRoutePrefixes(remoteConfig);
    if (configPrefixes.length > 0) {
      const configSet = new Set(configPrefixes);
      for (const mp of prefixes) {
        if (!configSet.has(mp)) {
          console.warn(
            `[ManifestLoader] ${remoteName}: manifest hostRoutePrefix "${mp}" is missing from federation-config ` +
              `hostRoutePrefix for this remote (add it so Next.js emits matching rewrites)`,
          );
          return null;
        }
      }
    }

    const pathOk = (p: string): boolean => pathUnderAnyHostRoutePrefix(p, prefixes);

    const prefixLabel = prefixes.join(', ');

    const validateMenuItemPaths = (item: ModuleManifestMenuItem, depth: number): boolean => {
      if (depth > 20) {
        console.warn(`[ManifestLoader] ${remoteName}: menu nesting exceeds max depth`);
        return false;
      }
      const hasChildren = item.children && item.children.length > 0;
      if (hasChildren) {
        for (const child of item.children!) {
          if (!validateMenuItemPaths(child, depth + 1)) {
            return false;
          }
        }
        if (item.path !== undefined && item.path !== '' && !pathOk(item.path)) {
          console.warn(
            `[ManifestLoader] ${remoteName}: menu path "${item.path}" must equal one hostRoutePrefix or be nested under it (${prefixLabel})`,
          );
          return false;
        }
        return true;
      }
      if (!item.path || !pathOk(item.path)) {
        console.warn(
          `[ManifestLoader] ${remoteName}: leaf menu item "${item.text}" needs a path under one of hostRoutePrefix (${prefixLabel})`,
        );
        return false;
      }
      return true;
    };

    for (const item of manifest.menu.items) {
      if (!validateMenuItemPaths(item, 0)) {
        return null;
      }
    }
    for (const route of manifest.routes) {
      if (!pathOk(route.path)) {
        console.warn(
          `[ManifestLoader] ${remoteName}: route path "${route.path}" must equal one hostRoutePrefix or be nested under it (${prefixLabel})`,
        );
        return null;
      }
    }

    if (prefixes.length === 1) {
      manifest.hostRoutePrefix = prefixes[0];
    } else {
      manifest.hostRoutePrefix = [...prefixes];
    }

    const parsedWidgets = parseModuleManifestWidgets(obj.widgets, remoteName, pathOk);
    if (parsedWidgets === null) {
      return null;
    }
    if (parsedWidgets !== undefined) {
      manifest.widgets = parsedWidgets;
      manifest.widgets.items.forEach((item, index) => {
        sanitizeCatalogDescriptions(item, `widgets.items[${index}]`);
      });
    } else {
      delete manifest.widgets;
    }

    return manifest;
  }

  /** Normalize browser path for manifest matching (drops legacy `/m` segment used by older manifests). */
  private normalizePublicModulePath(path: string): string {
    let p = path.replace(/\/{2,}/g, '/');
    if (p.startsWith('/m/')) {
      p = p.replace(/^\/m/, '') || '/';
    }
    return p;
  }

  getManifest(remoteName: string): LoadedModuleManifest | null {
    return this.cache.get(remoteName)?.data ?? null;
  }

  getAllManifests(): LoadedModuleManifest[] {
    return Array.from(this.cache.values()).map(c => c.data);
  }

  getMenuItems(): {
    remoteName: string;
    section?: string;
    icon?: ModuleManifestIcon;
    position?: number;
    items: ModuleManifestMenuItem[];
  }[] {
    return this.getAllManifests().map(loaded => ({
      remoteName: loaded.remoteName,
      section: loaded.manifest.menu.section,
      icon: loaded.manifest.menu.icon,
      position: loaded.manifest.menu.position ?? 50,
      items: loaded.manifest.menu.items,
    }));
  }

  getWidgets(): {
    remoteName: string;
    section?: string;
    groupKey?: string;
    position?: number;
    items: ModuleManifestWidgetItem[];
  }[] {
    return this.getAllManifests().flatMap(loaded => {
      const widgets = loaded.manifest.widgets;
      if (!widgets?.items.length) {
        return [];
      }
      return [
        {
          remoteName: loaded.remoteName,
          section: widgets.section,
          groupKey: widgets.groupKey,
          position: widgets.position ?? 50,
          items: widgets.items,
        },
      ];
    });
  }

  getRoutes(): { remoteName: string; route: ModuleManifestRoute }[] {
    return this.getAllManifests().flatMap(loaded =>
      loaded.manifest.routes.map(route => ({
        remoteName: loaded.remoteName,
        route,
      })),
    );
  }

  getRouteForPath(path: string): { remoteName: string; route: ModuleManifestRoute } | null {
    const normalized = this.normalizePublicModulePath(path);
    for (const loaded of this.getAllManifests()) {
      for (const route of loaded.manifest.routes) {
        if (this.matchRoute(route.path, normalized)) {
          return { remoteName: loaded.remoteName, route };
        }
      }
    }
    return null;
  }

  private matchRoute(pattern: string, path: string): boolean {
    const patternParts = pattern.split('/').filter(Boolean);
    const pathParts = path.split('/').filter(Boolean);

    if (patternParts.length !== pathParts.length) {
      return false;
    }

    return patternParts.every((part, i) => {
      if (part.startsWith(':')) {
        return true;
      }
      return part === pathParts[i];
    });
  }

  extractRouteParams(pattern: string, path: string): Record<string, string> {
    const normalizedPath = this.normalizePublicModulePath(path);
    const patternParts = pattern.split('/').filter(Boolean);
    const pathParts = normalizedPath.split('/').filter(Boolean);
    const params: Record<string, string> = {};

    patternParts.forEach((part, i) => {
      if (part.startsWith(':') && pathParts[i]) {
        params[part.slice(1)] = pathParts[i];
      }
    });

    return params;
  }

  clearCache(): void {
    this.cache.clear();
    this.loadPromise = null;
  }

  async checkHealth(): Promise<Record<string, { manifest: boolean; remoteEntry: boolean }>> {
    const remotes = federationConfigLoader.getAllRemotes();
    const health: Record<string, { manifest: boolean; remoteEntry: boolean }> = {};

    await Promise.all(
      Object.entries(remotes)
        .filter(([, config]) => !config.isLibrary)
        .map(async ([remoteName, remoteConfig]) => {
          const result = { manifest: false, remoteEntry: false };

          try {
            const { url } = resolveRemoteUrl(remoteConfig);
            const entryRes = await fetch(url, { method: 'HEAD', cache: 'no-store' });
            result.remoteEntry = entryRes.ok;
          } catch {
            // remains false
          }

          try {
            const manifestUrl = this.resolveManifestUrl(remoteConfig);
            if (manifestUrl) {
              const manifestRes = await fetch(manifestUrl, { method: 'HEAD', cache: 'no-store' });
              result.manifest = manifestRes.ok;
            }
          } catch {
            // remains false
          }

          health[remoteName] = result;
        }),
    );

    return health;
  }
}

export const manifestLoader = new ManifestLoader();
